export type Project = {
    slug: string
    title: string
    category: string
    date: string
    year: string
    location: string
    client: string
    image: string
    blurb: string
    story: string[]
    gallery: string[]
}

export type Post = {
    slug: string
    title: string
    date: string
    readTime: string
    image: string
    excerpt: string
    body: string[]
}

export const categories = ['All', 'Portrait', 'Lifestyle', 'Weddings', 'Events', 'Family'] as const

export const projects: Project[] = [
    {
        slug: 'morning-field-trip',
        title: 'Morning field trip',
        category: 'Lifestyle',
        date: '10.01.26',
        year: '2026',
        location: 'East Lothian',
        client: 'Personal series',
        image: '/images/cap-work-field.jpg',
        blurb: 'An hour after sunrise in the long grass, shot entirely on available light.',
        story: [
            'We met at the edge of a barley field at ten past six, mostly because that is when the light stops being blue and starts being gold, and it only does it for about forty minutes. No lights, no reflectors, nothing carried in but a camera and a flask.',
            'What I like about this set is how little direction it needed. Once somebody is standing in grass up to their waist with the sun behind them, they stop performing and start just being there. The best frame of the morning is the one where she turned to see where I had gone.'
        ],
        gallery: ['/images/cap-hat.jpg', '/images/landscape-2.jpg', '/images/cap-bento-2.jpg']
    },
    {
        slug: 'posing-on-a-chair',
        title: 'Posing on a chair',
        category: 'Portrait',
        date: '09.01.26',
        year: '2026',
        location: 'Bonnington Works',
        client: 'Editorial commission',
        image: '/images/cap-work-chair.jpg',
        blurb: 'One chair, one colour, and a studio afternoon spent finding the angle that worked.',
        story: [
            'The brief was a single prop and a single colour. That sounds restrictive until you realise a chair gives somebody something to do with their hands, their weight and their attention, which is most of what makes a portrait difficult.',
            'We shot for three hours and used about eight minutes of it. The rest was talking, moving the light a hand-width at a time, and waiting for the moment the pose stopped looking like a pose.'
        ],
        gallery: ['/images/cap-bento-1.jpg', '/images/studio-1.jpg', '/images/portrait-2.jpg']
    },
    {
        slug: 'annie-and-her-dog-star',
        title: 'Annie and her dog Star',
        category: 'Lifestyle',
        date: '07.01.26',
        year: '2026',
        location: 'Pentland Hills',
        client: 'Private commission',
        image: '/images/cap-work-dog.jpg',
        blurb: 'A walk the two of them do every Sunday, photographed once so it would keep.',
        story: [
            'Annie had been putting this off for a year. Her reason was that Star is fourteen, and her worry was that a session would feel like a goodbye. So we did not do a session. We did the walk they always do, and I stayed a few steps behind with a long lens.',
            'Nothing here is arranged. The hill did the composing, the weather did the mood, and the two of them did what they do every Sunday morning regardless of whether anybody is photographing it.'
        ],
        gallery: ['/images/landscape-1.jpg', '/images/landscape-3.jpg', '/images/street-2.jpg']
    },
    {
        slug: 'bride-and-groom',
        title: 'Bride and Groom',
        category: 'Weddings',
        date: '05.01.26',
        year: '2026',
        location: 'Midlothian',
        client: 'Wedding commission',
        image: '/images/cap-work-wedding.jpg',
        blurb: 'A winter wedding photographed almost entirely from the back of the room.',
        story: [
            'They asked for a day that did not feel photographed, which is the request I like most and the one that takes the most work. It means arriving early, learning the building, finding the three places the light will be good later, and then mostly staying out of the way.',
            'There are eleven frames in the final set that they have told me they look at regularly. Not one of them is a frame anybody posed for.'
        ],
        gallery: ['/images/wedding-2.jpg', '/images/wedding-3.jpg', '/images/cap-avatar-3.jpg']
    },
    {
        slug: 'the-long-table',
        title: 'The long table',
        category: 'Family',
        date: '18.12.25',
        year: '2025',
        location: 'Fife',
        client: 'Private commission',
        image: '/images/cap-family.jpg',
        blurb: 'Four generations, one kitchen, and the two hours either side of lunch.',
        story: [
            'Family sessions go wrong when they become a queue of arrangements. This one worked because we never left the house and never stopped the afternoon to take a picture. I photographed the cooking, the arguing about the cooking, and the twenty quiet minutes afterwards.',
            'The grandmother in the middle of the set had said at the start that she did not photograph well. She is in more of the final edit than anybody.'
        ],
        gallery: ['/images/cap-svc-lifestyle.jpg', '/images/portrait-3.jpg', '/images/street-3.jpg']
    },
    {
        slug: 'after-hours',
        title: 'After hours',
        category: 'Events',
        date: '02.11.25',
        year: '2025',
        location: 'Edinburgh',
        client: 'Studio commission',
        image: '/images/cap-svc-event.jpg',
        blurb: 'A launch night covered end to end, from the empty room to the last table standing.',
        story: [
            'Event work is a different discipline to portraiture. You get one pass at everything, the light is whatever somebody else decided it would be, and the only way to cover it properly is to know the running order better than the people running it.',
            'I shot this one from the walls. The client wanted the room to look full and the night to look like it was worth attending, and both of those are documentary problems rather than lighting problems.'
        ],
        gallery: ['/images/street-1.jpg', '/images/architecture-1.jpg', '/images/street-2.jpg']
    },
    {
        slug: 'a-quiet-hour',
        title: 'A quiet hour',
        category: 'Portrait',
        date: '21.10.25',
        year: '2025',
        location: 'Leith',
        client: 'Personal series',
        image: '/images/cap-intro-portrait.jpg',
        blurb: 'Sittings made in a bare studio with one window and no other equipment at all.',
        story: [
            'An ongoing series. One room, one window, one stool, and whoever agrees to sit in it for an hour. The constraint is the point: with nothing to adjust, the only variable left is the person.',
            'Most people need about twenty minutes before they forget the camera is there. The pictures I keep are almost always from the last third of the hour.'
        ],
        gallery: ['/images/cap-bento-1.jpg', '/images/studio-2.jpg', '/images/darkroom-1.jpg']
    },
    {
        slug: 'first-light',
        title: 'First light',
        category: 'Lifestyle',
        date: '30.09.25',
        year: '2025',
        location: 'North Berwick',
        client: 'Personal series',
        image: '/images/cap-mission.jpg',
        blurb: 'Swimmers on the east coast, photographed at the hour nobody else is awake for.',
        story: [
            'There is a group that swims off the same beach every morning of the year, and has done for a decade. I asked to come along for a week. They said I could as long as I got in.',
            'The pictures are better for it. You cannot photograph that particular kind of cold honestly from the sand.'
        ],
        gallery: ['/images/landscape-3.jpg', '/images/hero-2.jpg', '/images/cap-hero.jpg']
    }
]

export const services = [
    {
        title: 'Professional headshots',
        blurb: 'Stand out with a polished, professional look that still looks like you.',
        image: '/images/cap-svc-headshot.jpg'
    },
    {
        title: 'Events',
        blurb: 'Your evening, your running order, covered end to end without interrupting it.',
        image: '/images/cap-svc-event.jpg'
    },
    {
        title: 'Portrait photography',
        blurb: 'Capture your best self with beautifully composed, unhurried portraits.',
        image: '/images/cap-bento-1.jpg'
    },
    {
        title: 'Weddings',
        blurb: 'From the first look to the final dance, you will relive every moment of it.',
        image: '/images/cap-work-wedding.jpg'
    },
    {
        title: 'Family sessions',
        blurb: 'Celebrate the way your family actually is, not the way it poses.',
        image: '/images/cap-family.jpg'
    },
    {
        title: 'Lifestyle photography',
        blurb: 'Story-driven images of the ordinary days that turn out to matter most.',
        image: '/images/cap-svc-lifestyle.jpg'
    }
]

export const stats = [
    { value: 9659, suffix: '+', label: 'Photos taken, each telling a story of its own' },
    { value: 30, suffix: '%', label: 'Of clients book another session within the year' },
    { value: 100, suffix: '+', label: 'Satisfied clients across Scotland and beyond' }
]

export const turnaround = {
    label: 'Turnaround time',
    value: '7 days',
    blurb: 'Edited photographs delivered in seven days or less, every time.'
}

export const process = [
    {
        step: '01',
        title: 'We talk first',
        blurb: 'A call, no charge, where you tell me what the day is and I tell you honestly whether I am the right person for it.'
    },
    {
        step: '02',
        title: 'A plan you can read',
        blurb: 'You get a written outline: timings, locations, what I need from you, and exactly what it costs. Nothing moves until you agree to it.'
    },
    {
        step: '03',
        title: 'The shoot itself',
        blurb: 'I arrive early, learn the space, and then mostly stay out of the way. The less you notice me working, the better the pictures get.'
    },
    {
        step: '04',
        title: 'Edited and delivered',
        blurb: 'A private gallery within seven days, fully edited, downloadable at full resolution and yours to keep and print.'
    }
]

export const testimonials = [
    {
        quote: 'The photos turned out amazing. He captured the moment without it ever feeling staged or forced.',
        detail: 'Every image brings back the emotions of the day, and I could not be happier with how they came out.',
        name: 'Sarah and John',
        role: 'Newlyweds',
        avatar: '/images/cap-avatar-3.jpg',
        rating: '5.0'
    },
    {
        quote: 'I have never once liked a photograph of myself. I like four of these, which is four more than I expected.',
        detail: 'He spent the first twenty minutes just talking to me, and I think that is the whole trick.',
        name: 'Imogen Clarke',
        role: 'Headshot session',
        avatar: '/images/cap-avatar-1.jpg',
        rating: '5.0'
    },
    {
        quote: 'Booked him for a launch night and got back a set that made the room look exactly as good as it felt.',
        detail: 'Turned everything around in under a week, which nobody else we asked would commit to.',
        name: 'Daniel Okafor',
        role: 'Event commission',
        avatar: '/images/cap-avatar-2.jpg',
        rating: '5.0'
    }
]

/** The live-chat style exchange shown inside the bento grid. */
/**
 * The enquiry exchange rendered as a phone thread in the bento.
 *
 * `from` decides the side and the colour: 'them' is the client, arriving on
 * the left in grey, 'me' is the studio replying on the right in green. The
 * stamp is repeated above each turn in the conversation, exactly as a phone
 * does when a reply comes back in the same minute.
 */
export const chatThread = {
    stamp: 'Today 09:15',
    messages: [
        { from: 'them', text: 'What if I look awkward in photos?', stamped: true },
        { from: 'me', text: 'Everyone feels that way at first!', stamped: true },
        { from: 'me', text: 'We will do some test shots', stamped: false },
        {
            from: 'me',
            text: 'You will see how great you look with the right angles and lighting',
            stamped: false
        },
        { from: 'them', text: 'Perfect, thanks!', stamped: false }
    ]
} as const

/** The overlapping portrait stack beside the satisfied-clients count. */
export const clientAvatars = [
    { src: '/images/cap-avatar-1.jpg', alt: 'Portrait of a past client' },
    { src: '/images/cap-avatar-2.jpg', alt: 'Portrait of a past client' },
    { src: '/images/cap-avatar-3.jpg', alt: 'Portrait of a past client' },
    { src: '/images/cap-svc-headshot.jpg', alt: 'Portrait of a past client' },
    { src: '/images/cap-hat.jpg', alt: 'Portrait of a past client' }
]

export const faqs = [
    {
        question: 'How far in advance should I book?',
        answer: 'For weddings, three to six months is ideal, and summer Saturdays go a good deal earlier than that. Portrait and family sessions usually need two or three weeks. If your date is sooner than that, ask anyway, because cancellations happen more often than you would think.'
    },
    {
        question: 'What is your turnaround time?',
        answer: 'Edited photographs land in a private gallery within seven days of the shoot. For full wedding coverage it is closer to three weeks, and I will send a small preview set within forty-eight hours so you have something to look at in the meantime.'
    },
    {
        question: 'How long will it take to receive my photos?',
        answer: 'You get a sneak peek of a handful of frames within two days. The complete, fully edited gallery follows inside a week for sessions, and inside three weeks for weddings. Everything is downloadable at full resolution and stays online for a year.'
    },
    {
        question: 'How much does it cost?',
        answer: 'It depends on the type of work, how long the coverage runs, and how much travel is involved. Portrait sessions start at 280 pounds and wedding coverage starts at 1,600. You will always get a written quote before anything is booked, with no line items that appear later.'
    },
    {
        question: 'How many photos do I get?',
        answer: 'Between 50 and 100 fully edited images for a standard session, and 400 to 600 for a full wedding day. I would rather hand over a set where every frame earns its place than pad the count out.'
    }
]

export const posts: Post[] = [
    {
        slug: 'why-every-business-needs-professional-photography',
        title: 'Why every business needs professional photography',
        date: 'Oct 11, 2025',
        readTime: '6 min read',
        image: '/images/cap-blog-1.jpg',
        excerpt: 'Your website has about three seconds to look like it is run by people who care. Photography is most of that.',
        body: [
            'There is a particular look a business has when it is using stock photography, and everybody recognises it even if they could not tell you why. It is not that the images are bad. It is that they are clearly of somewhere else, and the gap between the picture and the place does the opposite of what it is supposed to.',
            'The practical argument is simpler than the aesthetic one. A shoot costs once and the images work for years, across your site, your proposals, your job adverts and every platform you post on. Spread across that, it is one of the cheaper things you will do.',
            'If you are going to do it, do it with a plan. Make a list of every place an image will end up, including the awkward shapes, and shoot for those specifically. A gorgeous frame that does not crop to a square is a frame you will not use.'
        ]
    },
    {
        slug: 'feeling-awkward-during-a-photoshoot',
        title: 'Feeling awkward or nervous during a photoshoot? You are not alone',
        date: 'Sep 30, 2025',
        readTime: '4 min read',
        image: '/images/cap-blog-2.jpg',
        excerpt: 'Almost nobody walks in comfortable. Here is what actually happens in the first twenty minutes.',
        body: [
            'The single most common thing said to me at the start of a session is some version of I am terrible at this. It is said by teachers, surgeons, people who speak in public for a living, and people who have been married thirty years. It is close to universal, which should tell you it is not really about you.',
            'What is happening is that you are being asked to do nothing, on purpose, while being watched. There is no task. Almost every awkward photograph is a picture of somebody who has not been given anything to do.',
            'So I give people something to do. Walk to that wall. Sit down properly, not the way you think you should sit. Tell me about the drive over. Somewhere in the middle of an ordinary sentence the shoulders drop, and that is the frame.'
        ]
    },
    {
        slug: 'what-to-wear-to-a-portrait-session',
        title: 'What to wear to a portrait session',
        date: 'Sep 12, 2025',
        readTime: '5 min read',
        image: '/images/cap-svc-headshot.jpg',
        excerpt: 'One rule does most of the work: wear the thing you already own and already like.',
        body: [
            'People buy something new for a shoot far more often than they should. New clothes have not been worn in, so you spend the session adjusting them, and adjusting is visible in every frame.',
            'Solid colours photograph better than tight patterns, mid-tones beat pure white and pure black, and anything with a visible logo will date the picture within two years. Beyond that, the honest advice is to bring two options and let the room decide.',
            'Bring layers regardless of the forecast. This is Scotland, and half of my favourite portraits involve a coat that was not part of the plan.'
        ]
    }
]

export const marqueeWords = [
    'Portraiture',
    'Weddings',
    'Family sessions',
    'Headshots',
    'Events',
    'Lifestyle',
    'Editorial',
    'Commissions'
]

export const gear = [
    { name: 'Canon EOS R5', role: 'Camera' },
    { name: 'Canon RF 24-70mm f/2.8L', role: 'Zoom lens' },
    { name: 'Sigma 85mm f/1.4 DG HSM Art', role: 'Prime lens' },
    { name: 'DJI Mavic Air 2', role: 'Drone' },
    { name: 'Godox AD200 Pro', role: 'Flash' },
    { name: 'Lightroom and Photoshop', role: 'Editing' }
]

export const clients = [
    'Scottish Ballet',
    'Hollow Press',
    'The Leith Collective',
    'Fettes College',
    'Blackwood Wines',
    'Assembly Rooms'
]
